// src/components/Auth/AuthWrapper.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Fade,
  useTheme,
  useMediaQuery,
  IconButton,
  Typography,
  Divider,
} from '@mui/material';
import {
  LightMode,
  DarkMode,
  Movie,
  People,
  Favorite,
} from '@mui/icons-material';
import { useApp } from '../../contexts/AppContext';
import { useAuth } from '../../contexts/AuthContext';
import { Hub } from 'aws-amplify/utils';
import { useNavigate, useLocation } from 'react-router-dom';
import SignIn from './SignIn';
import SignUp from './SignUp';
import AuthErrorBoundary from './AuthErrorBoundary';

const AuthWrapper = () => {
  console.log('🔑 AuthWrapper: Component rendered', { pathname: window.location.pathname });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { toggleTheme } = useApp();
  const { user, isAuthenticated, loading, userCreated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  console.log('🔑 AuthWrapper: Auth state', { user: !!user, isAuthenticated, loading });
  // Get initial auth mode from URL params
  const urlParams = new URLSearchParams(location.search);
  const initialMode = urlParams.get('mode') === 'signup' ? 'signup' : 'signin';
  const isExpired = urlParams.get('expired') === 'true';
  const [authMode, setAuthMode] = useState(initialMode);
  const [currentFeature, setCurrentFeature] = useState(0);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [showExpiredMessage, setShowExpiredMessage] = useState(isExpired);
  
  // Get return URL from location state
  const returnUrl = location.state?.from?.pathname || '/movies';

  // Check if user is already authenticated using AuthContext
  useEffect(() => {
    // Wait for AuthContext to finish loading
    if (loading) {
      console.log('🔑 AuthWrapper: AuthContext still loading...');
      return;
    }
    
    setCheckingAuth(false);
    
    if (isAuthenticated && !isExpired) {
      console.log('🔑 AuthWrapper: User already authenticated, redirecting to', returnUrl);
      navigate(returnUrl, { replace: true });
    } else {
      console.log('🔑 AuthWrapper: User not authenticated, showing auth form');
    }
    
    // Hide expired message after 5 seconds
    if (isExpired) {
      const timer = setTimeout(() => {
        setShowExpiredMessage(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [loading, isAuthenticated, isExpired, navigate, returnUrl]);

  // Listen for successful authentication
  useEffect(() => {
    const handleAuthSuccess = () => {
      console.log('🔑 AuthWrapper: Auth success, navigating to', returnUrl);
      // Navigate to return URL after successful auth
      navigate(returnUrl, { replace: true });
    };

    // Listen for auth events
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      console.log('🔑 AuthWrapper: Auth event', payload.event);
      if (payload.event === 'signedIn') {
        handleAuthSuccess();
      }
    });

    return unsubscribe;
  }, [navigate, returnUrl]);

  // Handle redirection after user creation
  useEffect(() => {
    if (userCreated && isAuthenticated && user) {
      console.log('🔑 AuthWrapper: New user created, redirecting to', returnUrl);
      setTimeout(() => {
        navigate(returnUrl, { replace: true });
      }, 2000); // 2 second delay to show welcome message
    }
  }, [userCreated, isAuthenticated, user, navigate, returnUrl]);

  const features = [
    {
      icon: <People sx={{ fontSize: 60, color: theme.palette.primary.main }} />,
      title: 'Connect with Friends',
      description: 'Follow your friends, share moments, and stay connected with the people who matter most.',
    },
    {
      icon: <Movie sx={{ fontSize: 60, color: theme.palette.secondary.main }} />,
      title: 'Discover Movies',
      description: 'Explore trending movies, find what\'s in theaters, and track your watchlist across all platforms.',
    },
    {
      icon: <Favorite sx={{ fontSize: 60, color: theme.palette.error.main }} />,
      title: 'Share Your Passion',
      description: 'Post about your favorite movies, share reviews, and discover what others are watching.',
    },
  ];

  // Rotate features every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % features.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [features.length]);

  const toggleAuthMode = () => {
    const newMode = authMode === 'signin' ? 'signup' : 'signin';
    setAuthMode(newMode);
    // Update URL without navigation
    const newUrl = new URL(window.location);
    newUrl.searchParams.set('mode', newMode);
    window.history.replaceState({}, '', newUrl);
  };

  // Show loading while AuthContext is loading
  if (loading || checkingAuth) {
    console.log('🔑 AuthWrapper: Checking authentication...', { loading, checkingAuth });
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
        }}
      >
        <Typography variant="h6" color="white">
          Checking authentication...
        </Typography>
      </Box>
    );
  }

  return (
    <AuthErrorBoundary>
      <Box
        sx={{
          minHeight: '100vh',
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: theme.spacing(2),
          position: 'relative',
          overflow: 'hidden',
        }}
      >
      {/* Background Animation */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1,
          background: theme.palette.mode === 'dark'
            ? `
              radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.3) 0%, transparent 50%)
            `
            : `
              radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)
            `,
          animation: 'float 6s ease-in-out infinite',
        }}
      />

      {/* Session Expired Message */}
      {showExpiredMessage && (
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1001,
            bgcolor: 'error.main',
            color: 'white',
            px: 3,
            py: 1,
            borderRadius: 2,
            boxShadow: 3,
            animation: 'slideDown 0.3s ease-out',
          }}
        >
          <Typography variant="body2" fontWeight="medium">
            Your session has expired. Please sign in again.
          </Typography>
        </Box>
      )}

      {/* Top Right Buttons */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 1000,
          display: 'flex',
          gap: 1,
        }}
      >
        {/* Movies Button */}
        <IconButton
          onClick={() => navigate('/movies')}
          title="Browse Movies"
          sx={{
            bgcolor: 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.4)',
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              transform: 'scale(1.05)',
            },
          }}
        >
          <Movie sx={{ color: 'white' }} />
        </IconButton>

        {/* Theme Toggle Button */}
        <IconButton
          onClick={toggleTheme}
          title="Toggle Theme"
          sx={{
            bgcolor: 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.4)',
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              transform: 'scale(1.05)',
            },
          }}
        >
          {theme.palette.mode === 'dark' ? (
            <LightMode sx={{ color: 'white' }} />
          ) : (
            <DarkMode sx={{ color: 'white' }} />
          )}
        </IconButton>
      </Box>

      {/* Desktop Layout */}
      {!isMobile && (
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 10 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            minHeight="calc(100vh - 64px)"
            gap={4}
          >
            {/* Left Side - Features */}
            <Box
              flex={1}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              sx={{
                color: 'white',
                textAlign: 'center',
                minWidth: 500,
                maxWidth: 700,
                px: 3,
              }}
            >
              {/* App Logo/Title */}
              <Box mb={4}>
                <Typography
                  variant="h2"
                  component="h1"
                  fontWeight="bold"
                  gutterBottom
                  sx={{
                    background: 'linear-gradient(45deg, #fff 30%, #f0f0f0 90%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  }}
                >
                  HypeCircle
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                  Connect, Share, and Discover Movies
                </Typography>
              </Box>

              {/* Feature Showcase */}
              <Box
                sx={{
                  height: 500,
                  width: 400,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                }}
              >
                {features.map((feature, index) => (
                  <Fade
                    key={index}
                    in={currentFeature === index}
                    timeout={500}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        textAlign: 'center',
                        width: '100%',
                        px: 4,
                        display: currentFeature === index ? 'block' : 'none',
                      }}
                    >
                      <Box mb={2}>
                        {feature.icon}
                      </Box>
                      <Typography
                        variant="h4"
                        fontWeight="bold"
                        gutterBottom
                        sx={{ mb: 3 }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          opacity: 0.9,
                          lineHeight: 1.8,
                          fontWeight: 400,
                          fontSize: '1.2rem',
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </Box>
                  </Fade>
                ))}
              </Box>

              {/* Feature Indicators */}
              <Box display="flex" gap={1} mt={2}>
                {features.map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: currentFeature === index ? 'white' : 'rgba(255, 255, 255, 0.3)',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                    }}
                    onClick={() => setCurrentFeature(index)}
                  />
                ))}
              </Box>
            </Box>

            {/* Right Side - Auth Forms */}
            <Box
              flex={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                minWidth: 380,
                maxWidth: 420,
              }}
            >
              <Paper
                elevation={20}
                sx={{
                  width: '100%',
                  borderRadius: 3,
                  overflow: 'hidden',
                  backdropFilter: 'blur(20px)',
                  background: theme.palette.mode === 'dark'
                    ? 'rgba(30, 30, 30, 0.9)'
                    : 'rgba(255, 255, 255, 0.95)',
                  border: `1px solid ${
                    theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.1)' 
                      : 'rgba(0, 0, 0, 0.05)'
                  }`,
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 8px 32px rgba(0, 0, 0, 0.5)'
                    : '0 8px 32px rgba(0, 0, 0, 0.1)',
                  position: 'relative',
                  zIndex: 50,
                }}
              >
                <Fade
                  in={authMode === 'signin'}
                  timeout={300}
                  mountOnEnter
                  unmountOnExit
                >
                  <Box display={authMode === 'signin' ? 'block' : 'none'}>
                    <SignIn onToggleAuthMode={toggleAuthMode} />
                  </Box>
                </Fade>

                <Fade
                  in={authMode === 'signup'}
                  timeout={300}
                  mountOnEnter
                  unmountOnExit
                >
                  <Box display={authMode === 'signup' ? 'block' : 'none'}>
                    <SignUp onToggleAuthMode={toggleAuthMode} />
                  </Box>
                </Fade>
              </Paper>
            </Box>
          </Box>
        </Container>
      )}

      {/* Mobile Layout */}
      {isMobile && (
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 10, minHeight: '100vh', display: 'flex', flexDirection: 'column', py: 2 }}>
          {/* Mobile Header */}
          <Box sx={{ textAlign: 'center', color: 'white', pt: 4, pb: 2 }}>
            <Typography
              variant="h3"
              component="h1"
              fontWeight="bold"
              gutterBottom
              sx={{
                background: 'linear-gradient(45deg, #fff 30%, #f0f0f0 90%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              }}
            >
              HypeCircle
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
              Connect, Share, and Discover Movies
            </Typography>
          </Box>

          {/* Mobile Features */}
          <Box sx={{ textAlign: 'center', color: 'white', py: 2, flex: '0 0 auto' }}>
            <Box mb={1}>
              {features[currentFeature].icon}
            </Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              {features[currentFeature].title}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '0.85rem', lineHeight: 1.4, px: 2 }}>
              {features[currentFeature].description}
            </Typography>
            
            {/* Mobile Feature Indicators */}
            <Box display="flex" gap={1} mt={2} justifyContent="center">
              {features.map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: currentFeature === index ? 'white' : 'rgba(255, 255, 255, 0.3)',
                    transition: 'all 0.3s ease',
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Mobile Divider */}
          <Divider 
            sx={{ 
              my: 2, 
              bgcolor: 'rgba(255, 255, 255, 0.2)', 
              width: '60%', 
              mx: 'auto' 
            }} 
          />

          {/* Mobile Auth Form */}
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-start', pt: 1, pb: 4 }}>
            <Paper
              elevation={20}
              sx={{
                width: '100%',
                borderRadius: 3,
                overflow: 'hidden',
                backdropFilter: 'blur(20px)',
                background: theme.palette.mode === 'dark'
                  ? 'rgba(30, 30, 30, 0.9)'
                  : 'rgba(255, 255, 255, 0.95)',
                border: `1px solid ${
                  theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.1)' 
                    : 'rgba(0, 0, 0, 0.05)'
                }`,
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 8px 32px rgba(0, 0, 0, 0.5)'
                  : '0 8px 32px rgba(0, 0, 0, 0.1)',
              }}
            >
              <Fade
                in={authMode === 'signin'}
                timeout={300}
                mountOnEnter
                unmountOnExit
              >
                <Box display={authMode === 'signin' ? 'block' : 'none'}>
                  <SignIn onToggleAuthMode={toggleAuthMode} />
                </Box>
              </Fade>

              <Fade
                in={authMode === 'signup'}
                timeout={300}
                mountOnEnter
                unmountOnExit
              >
                <Box display={authMode === 'signup' ? 'block' : 'none'}>
                  <SignUp onToggleAuthMode={toggleAuthMode} />
                </Box>
              </Fade>
            </Paper>
          </Box>
        </Container>
      )}



      {/* Global Styles for Animations */}
      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        @keyframes slideDown {
          0% {
            transform: translateX(-50%) translateY(-100%);
            opacity: 0;
          }
          100% {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
        }
      `}</style>
      </Box>
    </AuthErrorBoundary>
  );
};

export default AuthWrapper;